/*
 * Product page enhancements: the sticky add-to-cart bar and the live order
 * summary.
 *
 * Neither owns any cart or variant logic. The theme's <product-info> element
 * remains the single source of truth — this file reads the price it renders and
 * forwards clicks to the real submit button, so variant availability, quantity
 * rules and any options app all keep working untouched.
 */

/* -------------------------------------------------------------------------
 * Money formatting
 * ---------------------------------------------------------------------- */

/**
 * Format cents using the shop's own money_format string, so currency symbol
 * and separators match the rest of the storefront rather than being guessed.
 */
function bppFormatMoney(cents, format) {
  const value = (cents / 100).toFixed(2);
  const [whole, decimal] = value.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (!format) return `$${grouped}.${decimal}`;

  return format
    .replace(/\{\{\s*amount\s*\}\}/, `${grouped}.${decimal}`)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, grouped)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimal}`)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
}

/*
 * Fired on <product-info> by the order summary after every recalculation, so
 * anything else on the page showing the same figure can follow it instead of
 * computing its own. Declared before the sticky bar because defining the custom
 * element upgrades any instance already in the document synchronously, and its
 * connectedCallback subscribes to this name.
 */
const BPP_SUMMARY_EVENT = 'bpp:summary-update';

/* -------------------------------------------------------------------------
 * Sticky add-to-cart bar
 * ---------------------------------------------------------------------- */

class BppProductBar extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.formId = this.dataset.formId;

    this.priceTarget = this.querySelector('[data-bpp-bar-price]');
    this.qtyTarget = this.querySelector('[data-bpp-bar-qty]');
    this.submitProxy = this.querySelector('[data-bpp-bar-submit]');

    this.productInfo = this.closest('product-info');
    this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

    if (this.submitProxy) {
      this.submitProxy.addEventListener('click', () => this.mainButton?.click());
    }

    this.watchBuyButton();
    this.watchVariant();
    this.watchQuantity();
    this.sync();
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
    this.mutationObserver?.disconnect();
    if (this.unsubscribe) this.unsubscribe();
    if (this.productInfo && this.onProductInfoChange) {
      this.productInfo.removeEventListener('change', this.onProductInfoChange);
      this.productInfo.removeEventListener(BPP_SUMMARY_EVENT, this.onSummaryUpdate);
    }
    document.body.classList.remove('bpp-has-sticky-bar');
    document.documentElement.style.removeProperty('--bpp-sticky-bar-h');
  }

  /**
   * Only show the bar once the real add-to-cart button has scrolled out of
   * view — two identical buttons on screen at once is just noise.
   */
  watchBuyButton() {
    const anchor = this.productInfo?.querySelector('.bpp-pdp__buy');
    if (!anchor || !('IntersectionObserver' in window)) {
      this.setVisible(true);
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => this.setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 }
    );
    this.intersectionObserver.observe(anchor);
  }

  /**
   * product-info swaps the price node's contents on every variant change, so
   * mirroring it keeps the bar correct without re-deriving the price.
   */
  watchVariant() {
    const priceNode = document.getElementById(`price-${this.sectionId}`);
    if (!priceNode) return;

    this.mutationObserver = new MutationObserver(() => this.sync());
    // attributes matter too: when an option combination maps to no variant,
    // product-info calls setUnavailable() and returns without publishing
    // variantChange -- it only toggles a class on the price node. Without
    // watching attributes the sticky bar keeps the previous price and an
    // enabled buy proxy that clicks a disabled button and does nothing.
    this.mutationObserver.observe(priceNode, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => {
        // Let product-info finish its DOM swap first.
        requestAnimationFrame(() => this.sync());
      });
    }
  }

  /**
   * The figure in the bar is a running total, so it has to move on quantity as
   * well as on variant. Two sources, because neither is guaranteed on its own:
   * the order summary announces every recalculation it does, and the quantity
   * field is listened to directly for the case where the summary block is not
   * on the page at all.
   */
  watchQuantity() {
    if (!this.productInfo) return;

    this.onProductInfoChange = (event) => {
      if (event.target?.matches?.('.quantity__input, input[name="quantity"]')) this.sync();
    };
    this.onSummaryUpdate = () => this.sync();

    this.productInfo.addEventListener('change', this.onProductInfoChange);
    this.productInfo.addEventListener(BPP_SUMMARY_EVENT, this.onSummaryUpdate);
  }

  sync() {
    this.syncTotal();
    this.syncQuantity();
    this.syncSubmit();
  }

  /**
   * The figure in the bar is the same one the order summary already computed --
   * its estimated total when estimates are on, its subtotal when they are not.
   * Re-deriving it here would be a second implementation of the same sum that
   * could disagree with the panel a few hundred pixels above it. The rendered
   * price is the fallback for a page with no order-summary block.
   */
  syncTotal() {
    if (!this.priceTarget) return;

    const summary = this.productInfo?.querySelector('[data-bpp-order-summary]');
    const fromSummary =
      summary?.querySelector('[data-bpp-summary-total]')?.textContent.trim() ||
      summary?.querySelector('[data-bpp-summary-subtotal]')?.textContent.trim();
    if (fromSummary) {
      this.priceTarget.textContent = fromSummary;
      return;
    }

    const priceNode = document.getElementById(`price-${this.sectionId}`);
    const sale = priceNode?.querySelector('.price-item--sale, .price-item--regular');
    if (sale) this.priceTarget.textContent = sale.textContent.trim();
  }

  /**
   * Both plural forms come from Liquid, already translated, so the count is
   * re-rendered by swapping the digit inside the form that applies rather than
   * by pasting a number in front of a noun. English and French agree on the
   * boundary for every quantity this field can hold (its minimum is 1).
   */
  syncQuantity() {
    if (!this.qtyTarget) return;

    const input = this.productInfo?.querySelector('.quantity__input, input[name="quantity"]');
    const parsed = input ? parseInt(input.value, 10) : 1;
    const qty = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    const template = qty === 1 ? this.qtyTarget.dataset.countOne : this.qtyTarget.dataset.countOther;

    if (template) this.qtyTarget.textContent = template.replace(/\d+/, String(qty));
  }

  syncSubmit() {
    if (!this.submitProxy) return;

    this.submitProxy.toggleAttribute('disabled', Boolean(this.mainButton?.hasAttribute('disabled')));
    if (this.mainButton) {
      // textContent would take the arrow with it; only the text node before
      // the icon is replaced, so the icon survives every re-label.
      const label = this.mainButton.querySelector('span:not(.visually-hidden)');
      if (label && this.submitProxy.firstChild) {
        this.submitProxy.firstChild.textContent = `${label.textContent.trim()} `;
      }
    }
  }

  setVisible(visible) {
    this.dataset.visible = visible ? 'true' : 'false';
    document.documentElement.style.setProperty(
      '--bpp-sticky-bar-h',
      visible ? `${this.offsetHeight}px` : '0px'
    );
    document.body.classList.toggle('bpp-has-sticky-bar', visible);
  }
}

if (!customElements.get('bpp-product-bar')) {
  customElements.define('bpp-product-bar', BppProductBar);
}

/* -------------------------------------------------------------------------
 * Live order summary
 * ---------------------------------------------------------------------- */

class BppOrderSummary {
  constructor(root) {
    this.root = root;
    this.productInfo = root.closest('product-info');
    if (!this.productInfo) return;

    this.sectionId = this.productInfo.dataset.section;
    this.moneyFormat = root.dataset.moneyFormat || '';
    this.showEstimates = root.dataset.showEstimates === 'true';
    this.taxRate = parseFloat(root.dataset.taxRate) || 0;
    this.deliveryFee = parseFloat(root.dataset.deliveryFee);
    this.freeThreshold = parseFloat(root.dataset.freeThreshold);

    this.lineTarget = root.querySelector('[data-bpp-summary-line]');
    this.variantTarget = root.querySelector('[data-bpp-summary-variant]');
    this.addonsTarget = root.querySelector('[data-bpp-summary-addons]');
    this.subtotalTarget = root.querySelector('[data-bpp-summary-subtotal]');
    this.taxTarget = root.querySelector('[data-bpp-summary-tax]');
    this.deliveryTarget = root.querySelector('[data-bpp-summary-delivery]');
    this.totalTarget = root.querySelector('[data-bpp-summary-total]');

    this.bind();
    this.update();
  }

  bind() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(() => this.update()));
      subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.update());
    }

    this.productInfo.addEventListener('change', (event) => {
      if (event.target.matches('.quantity__input, input[name="quantity"]')) this.update();
      // Anything inside the options app's panel: a topping checked, a sauce
      // picked. The app recalculates the displayed price off the back of the
      // same event, so this is deliberately re-read on the next frame.
      if (event.target.closest('.bpp-pdp__app-block')) {
        requestAnimationFrame(() => this.update());
      }
    });

    // The options app owns its own markup and publishes no event this theme can
    // subscribe to. What it DOES do is rewrite the rendered price, so the price
    // node is the signal: when it changes, the surcharge changed.
    //
    // This is also the only way a click inside a custom control the app built
    // (a chip, a tile, a div with a handler) reaches the summary at all -- those
    // never fire a `change` event on an input.
    const priceNode = document.getElementById(`price-${this.sectionId}`);
    if (priceNode && 'MutationObserver' in window) {
      this.priceObserver = new MutationObserver(() => this.update());
      this.priceObserver.observe(priceNode, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // A custom control also has to be able to change the ADD-ON LIST, which is
    // read from the panel's own DOM rather than from an event.
    const appBlock = this.productInfo.querySelector('.bpp-pdp__app-block');
    if (appBlock && 'MutationObserver' in window) {
      this.appObserver = new MutationObserver(() => this.update());
      this.appObserver.observe(appBlock, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['checked', 'selected', 'value', 'class', 'aria-checked', 'aria-selected'],
      });
    }
  }

  /**
   * The current variant, looked up by id in the full variant list.
   *
   * The id input is the one thing product-info reliably keeps in step with the
   * customer's selection, so it is the source of truth here. The variant list
   * is parsed once and cached — it does not change within a page view.
   */
  variants() {
    if (this._variants) return this._variants;
    const script = this.productInfo.querySelector('script[data-bpp-variants]');
    if (!script) return (this._variants = []);
    try {
      this._variants = JSON.parse(script.textContent) || [];
    } catch (error) {
      console.error('Could not parse the variant list', error);
      this._variants = [];
    }
    return this._variants;
  }

  currentVariant() {
    const input = this.productInfo.querySelector('input[name="id"]');
    if (!input) return null;
    const id = String(input.value);
    return this.variants().find((v) => String(v.id) === id) || null;
  }

  quantity() {
    const input = this.productInfo.querySelector('.quantity__input, input[name="quantity"]');
    const value = input ? parseInt(input.value, 10) : 1;
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /**
   * Parse a rendered money string to cents.
   *
   * Whichever of . or , appears LAST is the decimal separator; the other groups
   * thousands. Assuming a dot was the bug that made "19,99 $" -- a
   * French-Canadian store's own format -- read as $1,999.00, and the whole order
   * summary was computed from it.
   */
  parseMoney(text) {
    if (!text) return null;
    const cleaned = text.replace(/[^\d.,]/g, '');
    if (!cleaned) return null;
    const decimalAt = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
    const digits =
      decimalAt === -1
        ? cleaned
        : cleaned.slice(0, decimalAt).replace(/[.,]/g, '') +
          '.' +
          cleaned.slice(decimalAt + 1).replace(/[.,]/g, '');
    const parsed = parseFloat(digits);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
  }

  /** The price the page is SHOWING, which is the one the app has adjusted. */
  renderedPriceCents() {
    const priceNode = document.getElementById(`price-${this.sectionId}`);
    const text = priceNode?.querySelector('.price-item--sale, .price-item--regular')?.textContent;
    return this.parseMoney(text);
  }

  /**
   * What one of these costs, INCLUDING whatever the options app has added.
   *
   * The variant JSON carries integer cents and is the trustworthy number, so it
   * is still the base. But a product-options app charges for extras by
   * rewriting the rendered price -- the theme never sees a variant for
   * "small with bacon and extra cheese" -- so a summary built from the variant
   * alone showed $17.00 while the page said $25.50. The rendered figure is what
   * the customer is agreeing to pay, so it wins when the two disagree.
   *
   * It only wins INSIDE A BAND, because parsing text is the fragile half of
   * this and a misparse here would now be believed rather than ignored. An
   * options app only ever adds, so a figure below the variant price is a
   * misparse; and a figure more than ten times the variant price is the
   * decimal-separator failure above (19,99 becoming 1999,00 is exactly 100x),
   * not a real basket of toppings. Outside the band the variant price stands.
   */
  unitPriceCents() {
    const variant = this.currentVariant();
    const base = variant && Number.isFinite(Number(variant.price)) ? Number(variant.price) : null;
    const rendered = this.renderedPriceCents();

    if (base === null) return rendered === null ? 0 : rendered;
    if (rendered === null || rendered === base) return base;
    if (rendered > base && rendered <= base * 10) return rendered;
    return base;
  }

  /**
   * Everything chosen inside the options app's panel, in the order the panel
   * shows it, for the line under the variant.
   *
   * The panel's markup belongs to the app, so this reads the only things every
   * options app must produce to submit anything at all: checked inputs and
   * selects with a value. Their labels come from whichever of the four usual
   * associations the app used. A control this cannot name is skipped rather
   * than guessed at, and a panel it can read nothing from leaves the line empty
   * -- which CSS then hides.
   */
  addons() {
    const panel = this.productInfo.querySelector('.bpp-pdp__app-block');
    if (!panel) return [];

    const labelFor = (input) => {
      const own = input.closest('label');
      if (own) {
        // A wrapping label contains the control; its text is the caption.
        const text = own.textContent.trim();
        if (text) return text;
      }
      if (input.id) {
        const tied = panel.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (tied?.textContent.trim()) return tied.textContent.trim();
      }
      const aria = input.getAttribute('aria-label');
      if (aria?.trim()) return aria.trim();
      if (typeof input.value === 'string' && input.value.trim()) return input.value.trim();
      return null;
    };

    const names = [];
    panel.querySelectorAll('input:checked').forEach((input) => {
      if (input.type !== 'checkbox' && input.type !== 'radio') return;
      const name = labelFor(input);
      if (name) names.push(name);
    });
    panel.querySelectorAll('select').forEach((select) => {
      const option = select.selectedOptions?.[0];
      const name = option?.textContent.trim();
      // A placeholder option carries no value; "Choose a sauce" is not a choice.
      if (name && option.value) names.push(name);
    });

    // Collapse the whitespace a wrapping label picks up from its own layout,
    // and drop repeats -- an app that renders both a visible and a
    // visually-hidden label for one control would otherwise list it twice.
    return [...new Set(names.map((n) => n.replace(/\s+/g, ' ').trim()).filter(Boolean))];
  }

  update() {
    const qty = this.quantity();
    const unit = this.unitPriceCents();
    const subtotal = unit * qty;

    if (this.lineTarget) this.lineTarget.textContent = bppFormatMoney(subtotal, this.moneyFormat);
    if (this.subtotalTarget) this.subtotalTarget.textContent = bppFormatMoney(subtotal, this.moneyFormat);

    if (this.variantTarget) {
      const variant = this.currentVariant();
      if (variant && variant.title && variant.title !== 'Default Title') {
        this.variantTarget.textContent = variant.title;
      }
    }

    if (this.addonsTarget) {
      this.addonsTarget.textContent = this.addons().join(' \u00b7 ');
    }

    if (!this.showEstimates) {
      this.announce();
      return;
    }

    const tax = Math.round(subtotal * (this.taxRate / 100));
    let delivery = 0;
    if (Number.isFinite(this.deliveryFee)) {
      const qualifiesFree =
        Number.isFinite(this.freeThreshold) && subtotal >= this.freeThreshold * 100;
      delivery = qualifiesFree ? 0 : Math.round(this.deliveryFee * 100);
    }

    if (this.taxTarget) this.taxTarget.textContent = bppFormatMoney(tax, this.moneyFormat);
    if (this.deliveryTarget) {
      this.deliveryTarget.textContent = delivery === 0
        ? this.deliveryTarget.dataset.freeLabel || bppFormatMoney(0, this.moneyFormat)
        : bppFormatMoney(delivery, this.moneyFormat);
    }
    if (this.totalTarget) {
      this.totalTarget.textContent = bppFormatMoney(subtotal + tax + delivery, this.moneyFormat);
    }

    this.announce();
  }

  announce() {
    this.productInfo.dispatchEvent(new CustomEvent(BPP_SUMMARY_EVENT));
  }

  /**
   * product-info re-renders this subtree on every option change, and an
   * observer left watching a detached node keeps that subtree alive for as long
   * as the page does. Called before a summary is replaced.
   */
  destroy() {
    this.priceObserver?.disconnect();
    this.appObserver?.disconnect();
  }
}

function bppInitOrderSummaries(scope = document) {
  scope.querySelectorAll('[data-bpp-order-summary]').forEach((node) => {
    if (node.dataset.bppInit === 'true') return;
    node.dataset.bppInit = 'true';
    node.bppSummary?.destroy();
    node.bppSummary = new BppOrderSummary(node);
  });
}

document.addEventListener('DOMContentLoaded', () => bppInitOrderSummaries());
document.addEventListener('shopify:section:load', (event) => bppInitOrderSummaries(event.target));
if (document.readyState !== 'loading') bppInitOrderSummaries();

/* -------------------------------------------------------------------------
 * Description clamp
 *
 * The design's product description is one line ("Pepperoni, ham, bacon,
 * italian sausage"). A real catalogue entry is not: the live Meat Lovers page
 * carries six SEO paragraphs, which pushed the price, the size picker and Add
 * to cart most of a screen down the buy column.
 *
 * The copy stays -- it is what search engines read -- and is collapsed instead.
 * The button is added ONLY when the text is genuinely taller than the clamp, so
 * a short description never grows a "Read more" it does not need. Everything
 * here is additive: with no JS the element never gets .is-collapsed and the
 * whole description shows, which is the safe direction to fail in.
 * ---------------------------------------------------------------------- */

function bppInitDescriptionClamps(scope = document) {
  scope.querySelectorAll('[data-bpp-clamp]').forEach((el) => {
    if (el.dataset.bppInit === 'true') return;
    el.dataset.bppInit = 'true';

    // Measure against the clamp before collapsing: scrollHeight on an already
    // collapsed -webkit-box is the clamped height, not the full one.
    el.classList.add('is-collapsed');
    const collapsed = el.clientHeight;
    el.classList.remove('is-collapsed');
    if (el.scrollHeight <= collapsed + 1) return;

    el.classList.add('is-collapsed');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bpp-pdp__description-toggle';
    button.setAttribute('aria-expanded', 'false');
    button.textContent = el.dataset.more || 'Read more';
    el.insertAdjacentElement('afterend', button);

    button.addEventListener('click', () => {
      const open = el.classList.toggle('is-collapsed') === false;
      button.setAttribute('aria-expanded', String(open));
      button.textContent = open ? el.dataset.less || 'Read less' : el.dataset.more || 'Read more';
    });
  });
}

document.addEventListener('DOMContentLoaded', () => bppInitDescriptionClamps());
document.addEventListener('shopify:section:load', (event) => bppInitDescriptionClamps(event.target));
if (document.readyState !== 'loading') bppInitDescriptionClamps();

/* -------------------------------------------------------------------------
 * App-block panel: hide it when the app shows nothing
 *
 * The panel frames whatever app blocks the merchant adds (today, Shopify
 * Subscriptions). An app block always renders its own wrapper, often with a
 * script or an empty container inside, even for a product it has nothing for
 * -- Subscriptions does exactly that for a product with no selling plan. The
 * CSS :empty / :has() test sees those wrappers as content, so customers saw
 * the panel's heading ("Customize your pizza") around an empty box.
 *
 * "Empty" here means nothing a customer can see or use: no visible text and no
 * visible image or control. The panel is re-checked whenever its content
 * changes, because apps often render after load, and it comes back the moment
 * an app shows something.
 * ---------------------------------------------------------------------- */

const BPP_APP_PANEL_VISIBLE =
  'img, svg, video, canvas, iframe, input:not([type="hidden"]), select, textarea, button, [role="radio"], [role="checkbox"], [role="button"]';

function bppAppPanelHasContent(panel) {
  const isOurs = (el) => el.closest('.bpp-pdp__app-block-title, script, style, template, noscript');

  const walker = document.createTreeWalker(panel, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const parent = walker.currentNode.parentElement;
    if (!walker.currentNode.textContent.trim() || !parent || isOurs(parent)) continue;
    if (parent.getClientRects().length && getComputedStyle(parent).visibility !== 'hidden') return true;
  }

  return [...panel.querySelectorAll(BPP_APP_PANEL_VISIBLE)].some((el) => {
    if (isOurs(el)) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 1 && rect.height > 1 && getComputedStyle(el).visibility !== 'hidden';
  });
}

function bppWatchAppPanels(scope = document) {
  scope.querySelectorAll('.bpp-pdp__app-block').forEach((panel) => {
    const check = () => {
      // Measured with the panel shown; the attribute goes back on in the same
      // task when it is still empty, so nothing is painted in between.
      panel.removeAttribute('data-bpp-empty');
      panel.toggleAttribute('data-bpp-empty', !bppAppPanelHasContent(panel));
    };
    check();

    if (panel.dataset.bppWatched || !('MutationObserver' in window)) return;
    panel.dataset.bppWatched = 'true';
    let frame = 0;
    new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(check);
    }).observe(panel, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
  });
}

document.addEventListener('DOMContentLoaded', () => bppWatchAppPanels());
document.addEventListener('shopify:section:load', (event) => bppWatchAppPanels(event.target));
if (document.readyState !== 'loading') bppWatchAppPanels();
